import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';
import { Phone, ArrowLeft, Loader2, Sparkles } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_aimarketplace-18/artifacts/7akyhfla_IMG_5649.jpg';

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestOTP, verifyOTP } = useAuth();
  
  const [step, setStep] = useState('phone'); // phone, otp
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoOtp, setDemoOtp] = useState('');

  const from = location.state?.from?.pathname || '/dashboard';

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const response = await requestOTP(phone);
      setDemoOtp(response.demo_otp);
      setStep('otp');
      toast.success('OTP sent to your phone');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter the complete OTP');
      return;
    }

    setLoading(true);
    try {
      await verifyOTP(phone, otp);
      toast.success('Welcome to Jambo!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background hero-pattern">
      {/* Header */}
      <header className="p-4">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <img src={LOGO_URL} alt="Jambo" className="h-10 w-auto" />
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md shadow-xl" data-testid="auth-card">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl jambo-gradient flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">
              {step === 'phone' ? 'Join Jambo' : 'Verify Your Phone'}
            </CardTitle>
            <CardDescription>
              {step === 'phone' 
                ? 'Enter your phone number to get started' 
                : `We sent a 6-digit code to ${phone}`}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {step === 'phone' ? (
              <form onSubmit={handleRequestOTP} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type="tel"
                      placeholder="+254 712 345 678"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="pl-10 h-12 text-lg"
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Include country code (e.g., +254 for Kenya)
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-full jambo-gradient text-white text-base"
                  disabled={loading}
                  data-testid="request-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Continue'
                  )}
                </Button>
              </form>
            ) : (
              <div className="space-y-5">
                <button 
                  onClick={() => setStep('phone')}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Change number
                </button>

                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={setOtp}
                    data-testid="otp-input"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} className="w-12 h-14 text-xl" />
                      <InputOTPSlot index={1} className="w-12 h-14 text-xl" />
                      <InputOTPSlot index={2} className="w-12 h-14 text-xl" />
                      <InputOTPSlot index={3} className="w-12 h-14 text-xl" />
                      <InputOTPSlot index={4} className="w-12 h-14 text-xl" />
                      <InputOTPSlot index={5} className="w-12 h-14 text-xl" />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                {/* Demo OTP Display */}
                {demoOtp && (
                  <div className="p-4 jambo-gradient-subtle rounded-xl text-center">
                    <p className="text-xs text-cyan-700 mb-1">Demo OTP (for testing)</p>
                    <p className="font-mono font-bold text-2xl jambo-gradient-text">{demoOtp}</p>
                  </div>
                )}

                <Button 
                  onClick={handleVerifyOTP}
                  className="w-full h-12 rounded-full jambo-gradient text-white text-base"
                  disabled={loading || otp.length !== 6}
                  data-testid="verify-otp-btn"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Verify & Continue'
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Didn't receive code?{' '}
                  <button 
                    onClick={handleRequestOTP}
                    className="text-cyan-600 hover:underline font-medium"
                    disabled={loading}
                  >
                    Resend
                  </button>
                </p>
              </div>
            )}

            {/* Terms */}
            <p className="text-xs text-center text-muted-foreground mt-6">
              By continuing, you agree to our{' '}
              <a href="#" className="text-cyan-600 hover:underline">Terms</a>
              {' '}and{' '}
              <a href="#" className="text-cyan-600 hover:underline">Privacy Policy</a>
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
